import {
  Branding,
  Document,
  Enquiry,
  InfoWorkType,
  WorkType,
} from '@kanda-libs/ks-frontend-services';
import { URLS } from 'config';
import {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FunctionComponent,
  type ReactNode,
} from 'react';
import { useSelector } from 'react-redux';
import { generatePath, useHistory, useLocation } from 'react-router-dom';
import { selectors } from 'store';
import { getEnquiryContent } from './helpers';

export interface EnquiryContextType {
  Content: ReactNode;
  enquiry: Enquiry | undefined;
  branding: Branding | undefined;
  infoWorkTypes: InfoWorkType[] | undefined;
  workTypes: WorkType[] | undefined;
  workType: WorkType | undefined;
  tradingName: string;
  hasLogo: boolean;
  logo: Document | undefined;
  id: string | undefined;
  isLoading: boolean;
  isSubmitting: boolean;
  setIsSubmitting: Dispatch<SetStateAction<boolean>>;
}

export const EnquiryContext = createContext<EnquiryContextType>({
  Content: null,
  enquiry: undefined,
  branding: undefined,
  infoWorkTypes: undefined,
  workTypes: undefined,
  workType: undefined,
  tradingName: '',
  logo: undefined,
  hasLogo: false,
  id: undefined,
  isLoading: true,
  isSubmitting: true,
  setIsSubmitting: () => {},
});

export const useEnquiryContext = () => useContext(EnquiryContext);

export interface EnquiryProviderProps {
  children: ReactNode;
}

const EnquiryProvider: FunctionComponent<EnquiryProviderProps> = function ({
  children,
}) {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { pathname } = useLocation();
  const { push } = useHistory();
  const enquiryInfoRequest = useSelector(
    selectors.infoEnquiry.getEntitiesAsArray,
  );
  const enquiryHasFetched = useSelector(selectors.infoEnquiry.getHasFetched);
  const id = useSelector(selectors.enquiry.getId);

  const enquiryInfo = useMemo(() => {
    if (enquiryInfoRequest.length === 0) return undefined;
    const filtered = enquiryInfoRequest.filter((val) => val.enquiry.id === id);
    return filtered?.[0] || undefined;
  }, [enquiryInfoRequest, id]);

  const enquiry = useMemo(() => enquiryInfo?.enquiry, [enquiryInfo]);

  const isLoading = useMemo(() => !enquiryInfo, [enquiryInfo]);

  const branding = useMemo(() => enquiryInfo?.branding, [enquiryInfo]);

  const tradingName = useMemo(
    () => enquiryInfo?.contact_info?.trading_name || 'your tradesperson',
    [enquiryInfo],
  );

  const infoWorkTypes = useMemo(
    () => enquiryInfo?.info_work_types,
    [enquiryInfo],
  );

  const workTypes = useMemo(() => enquiryInfo?.work_types, [enquiryInfo]);

  const workType = useMemo(
    () => enquiry?.enquiry_info?.job_info?.work_type,
    [enquiry],
  );

  const hasLogo = useMemo(() => {
    if (!branding) return false;
    return Boolean(branding?.logo?.id);
  }, [branding]);

  const logo = useMemo(() => {
    if (!branding || !hasLogo) return undefined;
    return branding.logo;
  }, [branding, hasLogo]);

  const Content = useMemo(
    () => getEnquiryContent(pathname, isLoading),
    [pathname, isLoading],
  );

  const values = useMemo(
    () => ({
      Content,
      enquiry,
      branding,
      infoWorkTypes,
      workTypes,
      workType,
      tradingName,
      hasLogo,
      logo,
      id,
      isLoading,
      isSubmitting,
      setIsSubmitting,
    }),
    [
      Content,
      enquiry,
      branding,
      infoWorkTypes,
      workTypes,
      workType,
      tradingName,
      hasLogo,
      logo,
      id,
      isLoading,
      isSubmitting,
      setIsSubmitting,
    ],
  );

  const initRef = useRef<boolean>(enquiryHasFetched);
  useEffect(() => {
    if (!enquiry || !id || initRef.current) return;
    initRef.current = true;
    const { current_state: state } = enquiry;
    if (
      state === 'viewed' ||
      state === 'contact_only' ||
      state === 'contact_agreed'
    )
      return;
    const hasQuote = Boolean(enquiry?.enquiry_info?.quote);
    if (hasQuote) {
      if (state === 'quoted' || state === 'ask_for_quote') {
        push(generatePath(URLS.enquiryQuoteConfirmDetails, { id }));
        return;
      }
      if (state === 'submitted' || state === 'converted_to_job') {
        push(generatePath(URLS.enquiryQuoteComplete, { id }));
        return;
      }
      return;
    }
    if (state === 'ask_for_quote' || state === 'preferences_set') {
      push(generatePath(URLS.enquiryProjectBudget, { id }));
      return;
    }
    if (state === 'submitted' || state === 'converted_to_job') {
      push(generatePath(URLS.enquiryComplete, { id }));
      // return;
    }
  }, [enquiry, id, push]);

  return (
    <EnquiryContext.Provider value={values}>{children}</EnquiryContext.Provider>
  );
};

export default EnquiryProvider;
